import React from "react";
import createIOSortType from "../../../../lib/createIOSortType";
import createGlobalState from "@ui/lib/createGlobalState";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import Ellipsis from "@ui/Ellipsis";
import { css } from "@linaria/core";
import { color, fonts } from "@ui";
import { colors, spacings } from "@ui/Theme";
import type { Project } from "../../../../api/useProjects";
import { useTranslations } from "../../../../api/useTranslations";
import type { Translation } from "../../../../api/useTranslations";
import useSortedData from "../../../../lib/hooks/useSortedData";
import classNames from "classnames";
import type { SortableValue } from "diagnostics-lib/comparator";
import t from "../../../../i18n";
import formatDate from "@ui/i18n/formatDate";
import { InformationLine } from "@ui/IconV2/react";
import formatNumber from "@ui/i18n/formatNumber";
import Text from "@ui/Text";
import TooltipsV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { millisecondsToFractional } from "../../Events/utils/millisecondsToFractional";
import { getEventStatusWeight } from "../../Events/utils/faultEventStatus";
import { badgeTextOverflow } from "@ui/Badge/_styles/BadgeTextOverflow";
import { DEFAULT_CONFIG, Markdown } from "@ui/Markdown";
import { type Comment } from "../../../../api/useComments";
import { analysisTitleShort } from "../../Analysis/analysisTitle";
import ChatFollowUpLine from "@ui/IconV2/react/ChatFollowUpLine";
import type { Index } from "diagnostics-lib/iterable";
import type { Options } from "@ui/Highcharts";
import TooltipV2 from "@ui/TooltipV2";
import {
  aggregationPeriodLabel,
  type AggregationPeriod,
} from "../../../../domain/aggregation/period";
import type { Quantity } from "../../../../api/useQuantities";
import PriorityFill from "@ui/IconV2/react/PriorityFill";
import type { Event } from "../../../../api/useEvents";
import { useLocation, useNavigate } from "react-router";
import { Tag } from "@ui/Tag";
import {
  getComputeRowKey,
  getEventId,
  getEventIdentifiedAction,
  getEventPriority,
  getEventSystems,
  isMultiFaultEvent,
} from "../../../../domain/event";
import { getEventPriorityText, gotoEventPage } from "../../Events/utils/event";
import { toBasenamePath } from "../../../../router/helpers/navigation";
import { BadgeEventStatus } from "../../Events/views/Events/BadgeEventStatus";
import EventTableSparkline from "../../Events/components/EventTableSparkline";
import { getLatestActiveAnalysisDate } from "../../latestActiveAnalysisDate";
import { EventName } from "../../Events/components/EventName";

const columns = [
  "priority",
  "name",
  "system",
  "status",
  "averageLosses",
  "startsAt",
  "action",
  "lastComment",
] as const;
export type Column = (typeof columns)[number];
const getPriorityTableColumnsLabel = (
  column: Column,
  translations: Translation,
  duration?: string,
) => {
  switch (column) {
    case "priority":
      return t("project.plant_state.block.priority_event.table.priority");
    case "name":
      return t("project.fault_events.table.live.metadata.name");
    case "system":
      return t("project.fault_events.table.live.metadata.system");
    case "status":
      return t("project.fault_events.table.live.metadata.status");
    case "startsAt":
      return t("project.fault_events.table.live.metadata.start_date");
    case "action":
      return t("project.fault_events.table.live.metadata.work_request");
    case "averageLosses":
      return t(
        "project.fault_events.table.live.metadata.average_losses",
        translations,
        duration ?? "",
      );
    case "lastComment":
      return "";
    default:
      return column;
  }
};

const useSortPlantStateBlockPriority = createGlobalState({
  storage: "session",
  key: "useSortPlantStateBlockPriority",
  type: createIOSortType(columns),
  defaultValue: { column: "priority", direction: "asc" },
  scoped: true,
});

interface P {
  project: Project;
  performanceIndicator: Quantity | null;
  events: Event[];
  sparklineStatisticByEventId: Index<{ id: string; mean: number }, string>;
  loading: boolean;
  sparklineOptionsByEventId: Index<{ id: string; options: Options }, string>;
  comments: Comment[];
  durationISO8601: AggregationPeriod;
  className?: string;
}

const HAS_COLUMN_AVERAGE_LOSS = "HAS_COLUMN_AVERAGE_LOSS";
const computeRowKey = (event: Event) => getComputeRowKey(event);
const isAroundZero = (mean: number) => {
  const absoluteValue = Math.abs(mean);
  return absoluteValue > 0 && absoluteValue < 0.01;
};

const PriorityTable = ({
  className,
  durationISO8601,
  project,
  performanceIndicator,
  events,
  comments,
  sparklineStatisticByEventId,
  sparklineOptionsByEventId,
  loading,
}: P) => {
  const translations = useTranslations();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lastCommentsByFaultEvent = React.useMemo(
    () =>
      comments
        .filter(
          (
            comment,
          ): comment is typeof comment & { faultEvent: NonNullable<typeof comment.faultEvent> } =>
            Boolean(comment.faultEvent),
        )
        .sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())
        .reduce<Record<string, Omit<(typeof comments)[number], "faultEvent">>>((acc, comment) => {
          const { faultEvent, ...commentBase } = comment;
          acc[faultEvent.id] = commentBase;
          return acc;
        }, {}),
    [comments],
  );

  const [sort, setSort] = useSortPlantStateBlockPriority({ scope: project.id });
  const latestAnalysisDate = React.useMemo(() => getLatestActiveAnalysisDate(project), [project]);
  const sortSelector: Record<Column, (e: Event) => SortableValue | null> = {
    priority: event => getEventPriorityText(getEventPriority(event)),
    name: event => event.name,
    startsAt: event => new Date().getTime() - new Date(event.startsAt).getTime(),
    status: event => getEventStatusWeight(event.metadata.status),
    system: event => {
      const systemIds = getEventSystems(event);
      return systemIds.length === 0
        ? null
        : systemIds.sort((a, b) => a.localeCompare(b)).join(", ");
    },
    action: event => getEventIdentifiedAction(event),
    averageLosses: event => {
      if (performanceIndicator === null) return null;

      const eventId = getEventId(event);
      const sparklineStatistic = sparklineStatisticByEventId.get(eventId);
      const sparklineCharts = sparklineOptionsByEventId.get(eventId);
      const mean = sparklineStatistic?.mean;
      if (sparklineStatistic === undefined || sparklineCharts === undefined || mean === undefined)
        return null;
      return sparklineStatistic.mean;
    },
    lastComment: () => null,
  };
  const sortedDatas = useSortedData(events, sortSelector[sort.column], sort.direction);

  const metadata: Record<Column, ColumnMetaData<Event>> = React.useMemo(
    () => ({
      priority: {
        label: getPriorityTableColumnsLabel("priority", translations),
        render: (event: Event) => {
          const priority = getEventPriority(event);
          return (
            <span className={priorityIcon}>
              <PriorityFill priority={priority} />
              <Text as="span">{getEventPriorityText(priority)}</Text>
            </span>
          );
        },
        minWidth: "124px",
        maxWidth: "124px",
        sortable: true,
        defaultSortDirection: "asc",
      },
      name: {
        label: getPriorityTableColumnsLabel("name", translations),
        render: (event: Event) => (
          <div className={nameWrapper}>
            <EventName name={event.name} hasMultiFault={isMultiFaultEvent(event)} />{" "}
            {event.type === "MANUAL" && <Tag size="s">{t("project.event.type.manual")}</Tag>}
          </div>
        ),
        minWidth: "300px",
        maxWidth: "500px",
        sortable: true,
        defaultSortDirection: "asc",
      },
      system: {
        label: (
          <span className={lineClamp}>{getPriorityTableColumnsLabel("system", translations)}</span>
        ),
        render: (event: Event) => {
          const systems = getEventSystems(event)
            .sort((a, b) => a.localeCompare(b))
            .join(", ");

          return systems ? <Ellipsis title={systems}>{systems}</Ellipsis> : null;
        },
        minWidth: "150px",
        maxWidth: "150px",
        sortable: true,
      },
      status: {
        label: getPriorityTableColumnsLabel("status", translations),
        render: (b: Event) => (
          <BadgeEventStatus className={badgeTextOverflow} status={b.metadata.status} />
        ),
        minWidth: "200px",
        maxWidth: "200px",
        sortable: true,
      },
      startsAt: {
        label: (
          <span className={lineClamp}>
            {getPriorityTableColumnsLabel("startsAt", translations)}
          </span>
        ),
        render: (b: Event) => {
          const duration = new Date().getTime() - new Date(b.startsAt).getTime();
          const fractional = millisecondsToFractional(duration);
          const date = formatDate(b.startsAt, "date", project.timezone);
          return (
            <div className={datetime}>
              <span>{date}</span>
              <span>{t("datetime.fractional", { type: "date", ...fractional })}</span>
            </div>
          );
        },
        minWidth: "200px",
        maxWidth: "200px",
        sortable: true,
      },
      averageLosses: {
        label: performanceIndicator ? (
          <div className={labelInfo}>
            <div className={lineClamp}>
              {getPriorityTableColumnsLabel(
                "averageLosses",
                translations,
                aggregationPeriodLabel(durationISO8601).toLocaleLowerCase(),
              )}
            </div>
            <TooltipV2
              placement="top"
              overlay={
                <OverlayWrapper
                  className={styledOverlayWrapper}
                  text={t(
                    "project.fault_events.table.live.average_losses.tooltip.info",
                    translations,
                    aggregationPeriodLabel(durationISO8601).toLocaleLowerCase(),
                    project.showPartLoadSensitiveKpis,
                  )}
                />
              }
            >
              <span className={centerIcon}>
                <InformationLine size="s" color={colors.carbon800} />
              </span>
            </TooltipV2>
          </div>
        ) : null,
        render: (event: Event) => {
          if (performanceIndicator === null) return null;

          const eventId = getEventId(event);
          const sparklineStatistic = sparklineStatisticByEventId.get(eventId);
          const charts = sparklineOptionsByEventId.get(eventId);

          if (sparklineStatistic === undefined || charts === undefined)
            return (
              <Text className={noSparkline}>
                {t(
                  "project.fault_events.table.average_losses.no_sparkline",
                  aggregationPeriodLabel(durationISO8601).toLocaleLowerCase(),
                )}
              </Text>
            );

          const { mean } = sparklineStatistic;
          const isAround = isAroundZero(mean);

          return (
            <div
              className={classNames(performanceIndicatorColumn, {
                [HAS_COLUMN_AVERAGE_LOSS]: true,
              })}
            >
              <div>
                {charts !== undefined && (
                  <EventTableSparkline
                    options={charts.options}
                    latestAnalysisDate={latestAnalysisDate}
                  />
                )}
              </div>
              {project.showPartLoadSensitiveKpis && (
                <Text>
                  {`${isAround ? "≈ " : ""}${formatNumber(mean, { precision: 1, sign: false })} ${performanceIndicator.unit || ""}`}
                </Text>
              )}
            </div>
          );
        },
        sortable: true,
        minWidth: "220px",
        maxWidth: "220px",
      },
      action: {
        label: (
          <div className={lineClamp}>{getPriorityTableColumnsLabel("action", translations)}</div>
        ),
        render: (event: Event) => {
          const actionText = getEventIdentifiedAction(event);
          return actionText ? (
            <TooltipsV2
              placement="bottom"
              overlay={
                <OverlayWrapper
                  className={styledOverlayWrapper}
                  text={<Markdown className={actionTooltip} defaultValue={actionText} />}
                />
              }
            >
              <div onClick={e => e.stopPropagation()}>
                <Markdown className={action} defaultValue={actionText} />
              </div>
            </TooltipsV2>
          ) : null;
        },
        minWidth: "300px",
        maxWidth: "500px",
        sortable: false,
      },
      lastComment: {
        label: "",
        render: (event: Event) => {
          switch (event.type) {
            case "DIAGNOSED": {
              const lastComment = lastCommentsByFaultEvent[event.id];
              if (lastComment === undefined) return <></>;
              return (
                <TooltipsV2
                  placement="bottom"
                  overlay={
                    <OverlayWrapper
                      className={overlayWrapperLastComment}
                      text={
                        <div>
                          <Text type="h4" className={tooltipTitleLastComment}>
                            {t(
                              "project.fault_events.table.live.metadata.last_comment.tooltip.title",
                            )}
                          </Text>
                          {t(
                            "project.fault_events.table.live.metadata.last_comment.tooltip.content",
                            {
                              analysis:
                                lastComment.analysisId && lastComment.metadata
                                  ? analysisTitleShort(
                                      {
                                        date: lastComment.metadata.date,
                                        group: lastComment.metadata.group,
                                        label: lastComment.metadata.label,
                                      },
                                      project.timezone,
                                    )
                                  : null,
                              author: lastComment.author.name ?? lastComment.author.id,
                              publishedAt: formatDate(lastComment.publishedAt, "relative", null),
                              content: (
                                <Markdown
                                  config={{
                                    ...DEFAULT_CONFIG,
                                    theme: {
                                      ...DEFAULT_CONFIG.theme,
                                      paragraph: "paragraph-body2",
                                    },
                                  }}
                                  className={actionTooltip}
                                  defaultValue={lastComment.content}
                                />
                              ),
                            },
                          )}
                        </div>
                      }
                    />
                  }
                >
                  <div className={iconComment}>
                    <ChatFollowUpLine size="m" color={color.carbon800} />
                  </div>
                </TooltipsV2>
              );
            }
            case "MANUAL":
            default:
              return null;
          }
        },
        minWidth: "50px",
        alignment: "right",
        sortable: false,
      },
    }),
    [
      performanceIndicator,
      project.timezone,
      translations,
      lastCommentsByFaultEvent,
      sparklineStatisticByEventId,
      sparklineOptionsByEventId,
      durationISO8601,
      latestAnalysisDate,
      project.showPartLoadSensitiveKpis,
    ],
  );

  const goToEvent = React.useCallback(
    (event: Event, e: React.MouseEvent) => {
      const targetPath = gotoEventPage(event);

      if (e.metaKey || e.ctrlKey) {
        window.open(toBasenamePath(targetPath));
      } else {
        void navigate(targetPath, { state: { from: pathname } });
      }
    },
    [navigate, pathname],
  );

  return (
    <Table
      className={className}
      stickyHeader={false}
      loading={loading}
      datas={sortedDatas}
      metadata={metadata}
      computeRowKey={computeRowKey}
      sort={sort}
      onSort={setSort}
      onRowClick={goToEvent}
    />
  );
};
export default PriorityTable;

//#region CSS
const performanceIndicatorColumn = css`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 60px;
  align-items: center;

  & > :nth-child(1) {
    width: 100%;
    height: 100%;
  }

  & > :nth-child(2) {
    margin: auto;
  }
`;

const iconComment = css`
  display: grid;
  place-items: center;
  padding: 10px 5px;
`;

const action = css`
  [data-lexical-editor] {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-box-orient: vertical;
  }
`;
const actionTooltip = css`
  [data-lexical-editor] {
    color: ${color.hydrogen};
  }
`;
const styledOverlayWrapper = css`
  max-width: 350px;
`;
const datetime = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xxs};

  span:last-child {
    font: ${fonts.body2};
    color: ${colors.carbon700};
  }
`;
const lineClamp = css`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  @supports (-webkit-line-clamp: 2) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;
const labelInfo = css`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
  & > :last-child {
    flex-shrink: 0;
  }
`;
const tooltipTitleLastComment = css`
  margin-bottom: ${spacings.xs};
`;
const overlayWrapperLastComment = css`
  width: 286px;
`;
const noSparkline = css`
  color: ${colors.carbon500};
`;
const centerIcon = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const priorityIcon = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
  color: ${color.carbon800};
`;
const nameWrapper = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
  overflow: auto;

  & > :first-child {
    flex: 1;
  }
`;
//#endregion CSS
